import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import CustomDatatable from "../CustomDatatable/CustomDatatable";
import type { ColumnMeta } from "../CustomDatatable/CustomDatatable";
import { RoleDialog, BulkRoleDialog, ConfirmDialog } from "../Dialogs/AccessDialogs";
import { useProjectRolesAPI } from "../../hooks/api/use-accessAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { ProjectRole } from "../../store/types";
import { BodyText } from "../../styles/Typography";
import { IconPlus } from "../../assets/icons/Icon";
import { FlexLineJustify, FlexLineGapSmall, Label } from "../../styles/PageLayoutStyles.styles";
import { toUserOptions } from "./accessOptions";
import { SectionText } from "./Access.styles";

/**
 * Каталог глобальных ролей продукта. Роли назначаются на конкретные инстансы
 * (страница инстанса) или сразу на все инстансы продукта (кнопки в строке).
 */
const ProjectRolesSection: React.FC<{ projectId: string }> = ({ projectId }) => {
  const {
    roles,
    refetchRoles,
    handleCreateRole,
    handleUpdateRole,
    handleDeleteRole,
    handleGrantOnAllInstances,
    handleRevokeOnAllInstances,
    toastRef,
  } = useProjectRolesAPI(projectId);
  const { users } = useUsersAPI();
  const usersOptions = useMemo(() => toUserOptions(users), [users]);

  const [roleDialog, setRoleDialog] = useState<{ visible: boolean; role: ProjectRole | null }>({
    visible: false,
    role: null,
  });
  const [deleting, setDeleting] = useState<ProjectRole | null>(null);
  const [bulk, setBulk] = useState<{ mode: "grant" | "revoke"; role: ProjectRole } | null>(null);

  const showInfo = (text: string) =>
    toastRef.current?.show({ severity: "success", summary: "Успешно", detail: text, life: 3000 });

  const columns: ColumnMeta<ProjectRole>[] = [
    { field: "name", header: "Роль", sortable: true, body: (role) => <code>{role.name}</code> },
    { field: "description", header: "Описание", body: (role) => role.description || "—" },
    {
      field: "actions",
      header: "Все инстансы продукта",
      body: (role) => (
        <FlexLineGapSmall>
          <Button
            label="Назначить"
            size="small"
            outlined
            onClick={() => setBulk({ mode: "grant", role })}
          />
          <Button
            label="Снять"
            size="small"
            outlined
            severity="secondary"
            onClick={() => setBulk({ mode: "revoke", role })}
          />
        </FlexLineGapSmall>
      ),
    },
  ];

  return (
    <>
      <Toast ref={toastRef} position="bottom-center" />
      <FlexLineJustify>
        <SectionText>
          <BodyText>
            Глобальные роли продукта. Имя роли попадает в токен инстанса, смысл ему придаёт само
            приложение.
          </BodyText>
        </SectionText>
        <Button onClick={() => setRoleDialog({ visible: true, role: null })}>
          <IconPlus size={16} />
          <Label>Новая роль</Label>
        </Button>
      </FlexLineJustify>

      <CustomDatatable<ProjectRole>
        data={roles}
        columns={columns}
        onEdit={(role) => setRoleDialog({ visible: true, role })}
        onDelete={setDeleting}
        getRowId={(role) => role.id}
        hideContextMenu
        sortable
      />

      <RoleDialog
        visible={roleDialog.visible}
        role={roleDialog.role}
        onHide={() => setRoleDialog({ visible: false, role: null })}
        onCreate={async (name, description) => {
          const ok = await handleCreateRole(name, description);
          if (ok) refetchRoles();
          return ok;
        }}
        onUpdate={async (role, description) => {
          const ok = await handleUpdateRole(role, description);
          if (ok) refetchRoles();
          return ok;
        }}
      />

      <ConfirmDialog
        visible={!!deleting}
        header="Удалить роль"
        text={`Роль «${deleting?.name}» будет удалена вместе со всеми её назначениями на всех инстансах.`}
        confirmLabel="Удалить"
        onHide={() => setDeleting(null)}
        onConfirm={async () => {
          const ok = await handleDeleteRole(deleting!);
          if (ok) refetchRoles();
          return ok;
        }}
      />

      <BulkRoleDialog
        visible={!!bulk}
        mode={bulk?.mode ?? "grant"}
        role={bulk?.role ?? null}
        usersOptions={usersOptions}
        onHide={() => setBulk(null)}
        onSubmit={async (userId) => {
          const count =
            bulk!.mode === "grant"
              ? await handleGrantOnAllInstances(bulk!.role.id, userId)
              : await handleRevokeOnAllInstances(bulk!.role.id, userId);

          if (count === null) return false;
          showInfo(
            bulk!.mode === "grant"
              ? `Роль назначена на инстансах: ${count}`
              : `Роль снята на инстансах: ${count}`
          );
          return true;
        }}
      />
    </>
  );
};

export default ProjectRolesSection;
